import { useEffect, useState } from 'react';

import {
  LookupAutocompleteView,
  mapDropdownOptions,
} from '@/shared/ui/molecules/LookupAutocomplete';
import { useDisciplineDropdown } from '../../../hooks/useDisciplineDropdown';

export interface DisciplineAutocompleteProps {
  /** ID da disciplina selecionada; `null` quando nada. */
  readonly value: number | null;
  /** Rótulo da seleção corrente (exibição inicial em edição). */
  readonly selectedLabel?: string;
  /** Notifica o novo ID e rótulo (`null`/`''` ao limpar). */
  readonly onChange: (id: number | null, label: string) => void;
  readonly label: string;
  readonly placeholder: string;
  readonly hasError?: boolean;
}

/**
 * Autocomplete de disciplina da prova (lookup `exam/discipline-dropdown-filtered`).
 * Envolve o `LookupAutocompleteView` compartilhado, que encapsula as armadilhas do
 * `Autocomplete` do ADS — em especial o reset do texto exibido ao limpar o campo.
 *
 * @example
 * ```tsx
 * <DisciplineAutocomplete
 *   value={form.disciplineId}
 *   selectedLabel={form.disciplineName ?? ''}
 *   onChange={(id, label) => onUpdate({ disciplineId: id, disciplineName: label })}
 *   label={t('form.fields.discipline')}
 *   placeholder={t('form.fields.disciplinePlaceholder')}
 * />
 * ```
 */
export function DisciplineAutocomplete({
  value,
  selectedLabel: selectedLabelProp = '',
  onChange,
  label,
  placeholder,
  hasError,
}: DisciplineAutocompleteProps) {
  const [search, setSearch] = useState('');
  const [inputText, setInputText] = useState(selectedLabelProp);
  const { disciplines, isQuerySuccess } = useDisciplineDropdown(search);

  // Sincroniza o rótulo exibido quando o pai o fornece após a montagem
  // (ex.: edição, cujo draft é populado depois que o autocomplete já montou).
  useEffect(() => {
    setInputText(selectedLabelProp);
  }, [selectedLabelProp]);

  return (
    <LookupAutocompleteView
      label={label}
      placeholder={placeholder}
      value={value !== null ? String(value) : ''}
      selectedLabel={inputText}
      options={mapDropdownOptions(disciplines)}
      isQuerySuccess={isQuerySuccess}
      search={search}
      hasError={hasError ?? false}
      onSearchChange={setSearch}
      onSelect={(id, itemLabel) => {
        onChange(id ? Number(id) : null, itemLabel);
        setInputText(itemLabel);
        setSearch('');
      }}
      onClear={() => {
        onChange(null, '');
        setInputText('');
        setSearch('');
      }}
    />
  );
}
